import stringify from 'fast-safe-stringify';

import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { PlatformService } from '@core/services/platform.service';
import { ServerTokensService } from '@core/services/server-tokens.service';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import { ServerEnvironments } from '../../../../server/server-environments.enum';
import { BUILD_NUMBER } from '../../app.tokens';
import { Client } from '@sentry/types';
import * as Sentry from '@sentry/browser';

@Injectable({
    providedIn: 'root'
})
export class LoggingService implements OnDestroy {
    public isDev: boolean;
    public sentryInstance?: Client;
    public aiInstance?: GenericObject;
    public readonly isVerbose: boolean;

    constructor(
        @Optional() @Inject(BUILD_NUMBER) private readonly buildNumber: string,
        @Optional() private readonly platformService: PlatformService,
        private readonly serverTokens: ServerTokensService
    ) {
        this.isVerbose = this.serverTokens.logVerbose;
        this.isDev = this.serverTokens.serverEnv === ServerEnvironments.Dev;
    }

    public initAI(): void {
        // Already initiated, silently eject
        if (this.aiInstance) {
            return;
        }

        // Client AI
        if (this.platformService.isBrowser) {
            if (this.serverTokens.enableAi) {
                const aiConfig: any = {
                    appId: 'SPA',
                    namePrefix: 'kopkande-ai-',
                    disableCorrelationHeaders: !this.serverTokens.logVerbose,
                    disableCookiesUsage: !this.serverTokens.logVerbose,
                    isRetryDisabled: !this.serverTokens.logVerbose,
                    enableAutoRouteTracking: this.serverTokens.logVerbose,
                    enableCorsCorrelation: this.serverTokens.logVerbose,
                    enableRequestHeaderTracking: this.serverTokens.logVerbose,
                    enableResponseHeaderTracking: this.serverTokens.logVerbose,
                    samplingPercentage: this.serverTokens.logVerbose ? 100 : 10
                };

                if (this.serverTokens.aiKey?.includes('InstrumentationKey=')) {
                    aiConfig.connectionString = this.serverTokens.aiKey;
                    console.warn('LOGGING-SERVICE', 'Application Insights is defined to use connectionString');
                } else if (this.serverTokens.aiKey) {
                    aiConfig.instrumentationKey = this.serverTokens.aiKey;
                    console.warn('LOGGING-SERVICE', 'Application Insights is defined to use instrumentationKey');
                } else {
                    console.warn(
                        'LOGGING-SERVICE',
                        'No Application Insights connection string or instrumentation key provided'
                    );
                    return;
                }

                this.aiInstance = new ApplicationInsights({ config: aiConfig });
                this.aiInstance.loadAppInsights();
                this.aiInstance.trackPageView();
            } else if (!this.isDev) {
                console.warn('LOGGING-SERVICE', 'Application Insights is not enabled');
            }
        }
    }

    public initSentry(): void {
        // Already initiated, silently eject
        if (this.sentryInstance || this.platformService.isServer) {
            return;
        }

        this.isDev = this.serverTokens.serverEnv === ServerEnvironments.Dev;

        // Setup sentry
        if (this.serverTokens.sentryKey) {
            this.sentryInstance = Sentry.init({
                dsn: this.serverTokens.sentryKey,
                release: this.buildNumber,
                debug: this.isVerbose,
                environment: this.serverTokens.serverEnv,
                sampleRate: 0.25,
                ignoreErrors: ['Non-Error exception captured']
            }) as Client | undefined;
            return;
        }

        if (!this.isDev) {
            console.warn('LOGGING-SERVICE', 'Sentry is not enabled');
        }
    }

    public ngOnDestroy(): void {
        if (this.aiInstance) {
            this.aiInstance.flush(false);
            this.aiInstance = undefined;
        }
        if (this.sentryInstance) {
            this.sentryInstance.close();
            this.sentryInstance = undefined;
        }
    }

    /**
     * Log message to Sentry
     * @param name
     * @param message
     */
    public logMessage(name: string, message: string, properties?: { [key: string]: string }): void {
        // Sentry
        if (this.sentryInstance) {
            let sentryMsg: string = `${BUILD_NUMBER} | ${name} - ${message}`;
            if (properties) {
                sentryMsg += ` - ${stringify(properties)}`;
            }
            this.sentryInstance.captureMessage(sentryMsg);
        }

        // Application Insights
        if (this.serverTokens.enableAi && this.aiInstance) {
            properties = properties || {};
            properties.message = message;
            this.aiInstance.trackEvent({ name: `${BUILD_NUMBER} | ${name}`, properties });
            this.aiInstance.flush();
        }
    }
}
